/**
 * Auth Repository
 * Handles all authentication-related API calls
 */

import { apiClient } from '../client'
import { Tenant } from './tenant.repository'

export interface LoginRequest {
  username: string
  password: string
}

export interface LoginUser {
  id: number
  username: string
  en_name: string
  ar_name?: string
  email?: string
  role_id: number
  tenant_id?: number
  home_tenant_id?: number
  acting_tenant_id?: number | null
  is_platform_admin?: boolean
  needs_tenant_selection?: boolean
  report_to: number
  password_changed?: number
  tenants: Tenant[]
  available_tenants?: Tenant[]
  permissions?: Record<string, string[]>
}

export interface LoginSuccessResponse {
  message: string
  data: {
    access_token: string
    user: LoginUser
  }
  mfa_required?: false
  mfa_setup_required?: false
}

export interface MfaChallengeResponse {
  message: string
  mfa_required?: boolean
  mfa_setup_required?: boolean
  mfa_token: string
  qr_data_url?: string | null
  otpauth_url?: string
  mfa_secret?: string
  username?: string
}

export type LoginResponse = LoginSuccessResponse | MfaChallengeResponse

export interface MfaVerifyRequest {
  mfa_token: string
  code: string
}

export interface UserProfile {
  id: number
  username: string
  en_name: string
  ar_name?: string
  email?: string
  role_id: number
  tenant_id?: number
  home_tenant_id?: number
  acting_tenant_id?: number | null
  is_platform_admin?: boolean
  needs_tenant_selection?: boolean
  report_to: number
  password_changed?: number
  tenants: Tenant[]
  available_tenants?: Tenant[]
  permissions?: Record<string, string[]>
}

export interface VerifyTokenResponse {
  valid: boolean
  message: string
}

class AuthRepository {
  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return apiClient.post<LoginResponse>('/auth/login', credentials, false)
  }

  async verifyMfa(body: MfaVerifyRequest): Promise<LoginSuccessResponse> {
    return apiClient.post<LoginSuccessResponse>('/auth/mfa/verify', body, false)
  }

  /**
   * Logout user
   */
  async logout(): Promise<{ message: string }> {
    // Send empty body to ensure proper request formatting
    return apiClient.post<{ message: string }>('/auth/logout', {})
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<{ user: UserProfile }> {
    return apiClient.get<{ user: UserProfile }>('/auth/me')
  }

  async switchTenant(tenant_id: number): Promise<LoginSuccessResponse> {
    return apiClient.post<LoginSuccessResponse>('/auth/switch-tenant', { tenant_id })
  }

  async changePassword(current_password: string, new_password: string) {
    return apiClient.post<{ message: string; password_changed: number }>('/auth/change-password', {
      current_password,
      new_password,
    })
  }

  async updateProfile(data: { email?: string; profile_pic?: string }) {
    return apiClient.post<{ user: UserProfile }>('/auth/profile', data)
  }

  /**
   * Verify token validity
   */
  async verifyToken(): Promise<VerifyTokenResponse> {
    return apiClient.get<VerifyTokenResponse>('/auth/verify')
  }
}

// Export singleton instance
export const authRepository = new AuthRepository()

